import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import type { IPrinterConfigurationDto } from "../../../../Objects/PrintAutomation/PrintAutomation"
import { usePrintAutomation } from "../../../../hooks/PrintAutomation/usePrintAutomation"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import { faClose, faGear, faPrint } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../../../components/Button/Button"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import InputField from "../../../../components/InputField/InputField"
import { SimpleToggle } from "../../../../components/SimpleToggle/SimpleToggle"
import Button from "../../../../components/Button/Button"

type Props = {
  printer: IPrinterConfigurationDto | null
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

const toPaperWidth = (value: number) => (Number.isFinite(value) && value > 0 ? value : 80)

export const PrinterSettingsPopup: React.FC<Props> = ({ printer, isOpen, onClose, onSaved }) => {
  const { t } = useTranslation(["pages"])

  const { savePrinter } = usePrintAutomation()

  const [form, setForm] = useState<IPrinterConfigurationDto | null>(printer)

  useEffect(() => {
    setForm(printer ? { ...printer, paperWidth: toPaperWidth(printer.paperWidth) } : null)
  }, [printer])

  if (!isOpen || !form) return null

  const handleToggle = (field: keyof IPrinterConfigurationDto) => {
    setForm((previous) => (previous ? { ...previous, [field]: !previous[field] } : previous))
  }

  const handleSave = async () => {
    if (!form?.id || !form.isAgentActive) return
    await savePrinter({
      ...form,
      isEnabled: form.isEnabled && !!form.isAgentActive,
      paperWidth: toPaperWidth(form.paperWidth),
    })
    onSaved()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_print_automation_config_popup_title")}
              icon={faPrint}
              path={form.displayName}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerSettingsPopup.button.onClose"
                />
              }
            />

            <div className="mt-4 space-y-3 p-1">
              <InputField
                type="number"
                title={t("label_print_automation_paper_width")}
                value={String(form.paperWidth)}
                icon={faGear}
                placeholder={t("label_print_automation_paper_width")}
                onInvalidMessage={t("label_print_automation_paper_width")}
                onChange={(event) =>
                  setForm((previous) => (previous ? { ...previous, paperWidth: Number(event.target.value) } : previous))
                }
                descriptionKey="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerSettingsPopup.input.printAutomationPaperWidth"
              />
            </div>

            <div className="mt-2 grid gap-2 px-1">
              {[
                ["includeCustomerData", "label_print_automation_include_customer_data"],
                ["includeAddress", "label_print_automation_include_address"],
                ["includeObservation", "label_print_automation_include_observation"],
                ["includeAdditionals", "label_print_automation_include_additionals"],
                ["includePaymentMethod", "label_print_automation_include_payment_method"],
                ["includeTotal", "label_print_automation_include_total"],
                ["kitchenItemsOnly", "label_print_automation_kitchen_items_only"],
                ["autoPrintOnNewOrder", "label_print_automation_auto_print_on_new_order"],
              ].map(([field, label]) => (
                <SimpleToggle
                  key={field}
                  value={Boolean(form[field as keyof IPrinterConfigurationDto])}
                  title={t(label)}
                  onChange={() => handleToggle(field as keyof IPrinterConfigurationDto)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerSettingsPopup.toggle.t"
                />
              ))}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <section className={defaultStyle.buttonsController}>
              <Button
                label={t("buttons:button_save")}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                onClick={() => void handleSave()}
                descriptionKey="interactionDescriptions:pages.myRestaurant.printAutomation.components.printerSettingsPopup.button.save"
              />
            </section>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
